import { z } from 'zod';
import { isoDate, uuid } from './common';
import { meetingSummary } from './meetings';

/**
 * Channels (PRD 58) — Fellow's shape: a named group of people in a workspace,
 * and the meetings posted into it. Posting a meeting shares it with every
 * member; a public channel is one anybody in the workspace may find and join.
 */
export const channelVisibility = z.enum(['PUBLIC', 'PRIVATE']);
export type ChannelVisibility = z.infer<typeof channelVisibility>;

export const channelRole = z.enum(['OWNER', 'MEMBER']);

export const channelSummary = z.object({
  id: uuid,
  name: z.string(),
  description: z.string().nullable(),
  visibility: channelVisibility,
  memberCount: z.number().int(),
  /** Meetings posted into it — how many, not which: a stranger to it learns no more. */
  meetingCount: z.number().int(),
  lastPostedAt: isoDate.nullable(),
  /** This person's place in it; null when they have not joined. */
  myRole: channelRole.nullable(),
  /** The series it follows — told to its members only. */
  series: z.array(z.string()),
});
export type ChannelSummary = z.infer<typeof channelSummary>;

export const channelsList = z.object({
  /** The channels this person is in. */
  mine: z.array(channelSummary),
  /** Public channels in their workspace they have not joined. */
  others: z.array(channelSummary),
});
export type ChannelsList = z.infer<typeof channelsList>;

export const channelMember = z.object({
  userId: uuid,
  name: z.string().nullable(),
  email: z.string(),
  role: channelRole,
});
export type ChannelMember = z.infer<typeof channelMember>;

/** A meeting in a channel: the row, who posted it, and whether this reader may take it out. */
export const channelMeeting = meetingSummary.extend({
  postedBy: z.string().nullable(),
  postedAt: isoDate.nullable(),
  canRemove: z.boolean(),
});
export type ChannelMeeting = z.infer<typeof channelMeeting>;

export const channelDetail = channelSummary.extend({
  members: z.array(channelMember),
  /** The series it follows: every meeting of each is posted as it happens. */
  series: z.array(
    z.object({ seriesKey: z.string(), title: z.string(), addedBy: z.string().nullable() }),
  ),
  /** Newest first. Empty for somebody who has not joined: joining is what shows them. */
  meetings: z.array(channelMeeting),
  canManage: z.boolean(),
  canJoin: z.boolean(),
});
export type ChannelDetail = z.infer<typeof channelDetail>;

export const createChannelBody = z.object({
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(300).nullable().optional(),
  visibility: channelVisibility.default('PUBLIC'),
});
export type CreateChannelBody = z.infer<typeof createChannelBody>;

export const updateChannelBody = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  description: z.string().trim().max(300).nullable().optional(),
  visibility: channelVisibility.optional(),
});
export type UpdateChannelBody = z.infer<typeof updateChannelBody>;

export const addChannelMemberBody = z.object({ email: z.string().email().max(320) });
export type AddChannelMemberBody = z.infer<typeof addChannelMemberBody>;

export const postToChannelBody = z.object({ meetingId: uuid });
export type PostToChannelBody = z.infer<typeof postToChannelBody>;

export const followSeriesBody = z.object({ seriesKey: z.string().trim().min(1).max(200) });
export type FollowSeriesBody = z.infer<typeof followSeriesBody>;

/** What following a series did at once: how many of its meetings it posted. */
export const seriesFollowed = z.object({ posted: z.number().int() });
export type SeriesFollowed = z.infer<typeof seriesFollowed>;
